import React, { useState, useEffect } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { fetchNewPatients } from "../../services/newPatientsService";
import { fetchPatientsFollowUp } from "../../services/patientFollowUp";
import { fetchPatientToCallBack } from "../../services/patienttoCallBack";
import UpdateCallLeads from "../calls/updateCallLeads";
import { useAppSelector } from "../../store/hook";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";

interface NewPatient {
  id: number;
  name: string;
  date: string;
  source: string;
  hasRecording: boolean;
  value: number;
}

type Props = {
  isPrintMode: boolean;
  clinicUniqueId: string;
  dateFilter: { startDate: string; endDate: string };
};

const PatientFollowUp = ({
  isPrintMode,
  clinicUniqueId,
  dateFilter,
}: Props) => {
  const clinics = useAppSelector((state) => state.user.user.clinics);
  const clinicDatax = clinics?.find(
    (item: any) => item.uniqueId === clinicUniqueId
  );
  const [rows, setRows] = useState<NewPatient[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [callLeadsModal, setCallLeadsModal] = useState(false);
  const [patientToCallBackRows, setPatientToCallBackRows] = useState<
    NewPatient[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const res = await fetchPatientsFollowUp({
        clinicUniqueId,
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      });
      if (res?.status?.msg === "Success") {
        setRows(
          res?.leads?.map((x: any, i: number) => ({
            ...x,
            name: `${x?.fname} ${x?.lname}`,
            srNo: i + 1,
            // id: i + 1,
            id: x?.id,

            customer_name_view: x?.callData?.customer_name,
            customer_phone_number_view: x?.callData?.customer_phone_number,
            customer_city_view: x?.callData?.customer_city,
            customer_state_view: x?.callData?.customer_state,
          }))
        );
      }
    } catch (err) {
      setError("Failed to fetch new patients data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (clinicUniqueId && dateFilter?.startDate && dateFilter?.endDate) {
      fetchData();
    }
  }, [clinicUniqueId, dateFilter]);

  const fetchDatax = async () => {
    try {
      const res = await fetchPatientToCallBack({
        clinicUniqueId,
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      });
      if (res?.status?.msg === "Success") {
        setPatientToCallBackRows(
          res?.leads?.map((x: any, i: number) => ({
            ...x,
            name: `${x?.fname} ${x?.lname}`,
            srNo: i + 1,
            id: x?.id,
            customer_name_view: x?.callData?.customer_name,
            customer_phone_number_view: x?.callData?.customer_phone_number,
            customer_city_view: x?.callData?.customer_city,
            customer_state_view: x?.callData?.customer_state,
          }))
        );
      }
    } catch (err) {
      setError("Failed to fetch new patients data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBothAPI = () => {
    fetchData();
    fetchDatax();
  };
  useEffect(() => {
    if (clinicUniqueId && dateFilter?.startDate && dateFilter?.endDate) {
      fetchBothAPI();
    }
  }, [clinicUniqueId, dateFilter]);

  const handleRecordingClick = (id: number) => {
    alert(`Play recording for patient ID: ${id}`);
  };

  const renderSource = (source: string, hasRecording: boolean, id: number) => {
    switch (source) {
      case "Call":
        return (
          <span>
            📞{" "}
            {hasRecording && (
              <span
                onClick={() => handleRecordingClick(id)}
                style={{ cursor: "pointer" }}
                title="Click to listen to recording"
              >
                🔊
              </span>
            )}
          </span>
        );
      case "Booking":
        return <span>📅</span>;
      case "Rewards":
        return <span>🎁</span>;
      case "Referral":
        return <span>👥</span>;
      default:
        return source;
    }
  };

  const reasonBodyTemplate = (row: any) => (
    <div
      style={{
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        lineHeight: "1.5",
      }}
    >
      {row.notes}
    </div>
  );

  const actionBodyTemplate = (row: any) => (
    <div className="flex justify-center items-center h-full">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setSelectedRow({ ...row, newStatus: row.status });
          setCallLeadsModal(true);
        }}
        className="px-2 py-1 text-xs rounded bg-[#4CC9C0] text-white whitespace-nowrap min-w-[80px] hover:opacity-90"
      >
        📋 Update
      </button>
    </div>
  );

  const actionBodyTemplate2 = (row: any) => (
    <div className="flex justify-center items-center h-full">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setSelectedRow({ ...row, newStatus: row.status });
          setCallLeadsModal(true);
        }}
        className="px-2 py-1 text-xs rounded bg-[#4CC9C0] text-white whitespace-nowrap min-w-[80px] hover:opacity-90"
      >
        📋 Update
      </button>
    </div>
  );

  // const columns: Column[] = [
  //   {
  //     field: "srNo",
  //     headerName: "#",
  //     width: 40,
  //     sortable: true,
  //     // For automatic serial number with pagination
  //     // renderCell: (_row, index) => index + 1,
  //   },
  //   {
  //     field: "customer_phone_number_view",
  //     headerName: "Phone",
  //     minWidth: 120,
  //     sortable: true,
  //   },
  //   {
  //     field: "customer_city_view",
  //     headerName: "City",
  //     minWidth: 120,
  //     sortable: true,
  //   },
  //   {
  //     field: "customer_state_view",
  //     headerName: "State",
  //     minWidth: 120,
  //     sortable: true,
  //   },
  //   {
  //     field: "status",
  //     headerName: "Status",
  //     minWidth: 120,
  //     sortable: true,
  //   },
  //   {
  //     field: "notes",
  //     headerName: "Notes",
  //     minWidth: 200,
  //     renderCell: (row) => (
  //       <div
  //         style={{
  //           whiteSpace: "pre-wrap",
  //           wordWrap: "break-word",
  //           width: "100%",
  //           padding: "8px 0",
  //           lineHeight: "1.5",
  //         }}
  //       >
  //         {row.notes}
  //       </div>
  //     ),
  //   },
  //   {
  //     field: "source",
  //     headerName: "Source",
  //     minWidth: 120,
  //     sortable: true,
  //     renderCell: (row) => <span>{row.source}</span>,
  //   },
  //   {
  //     field: "action",
  //     headerName: "Action",
  //     width: 120,
  //     sortable: false,
  //     renderCell: (row) => (
  //       <div className="flex justify-center items-center h-full">
  //         <button
  //           onClick={(e) => {
  //             e.stopPropagation();
  //             setSelectedRow({ ...row, newStatus: row.status });
  //             setCallLeadsModal(true);
  //           }}
  //           className="px-2 py-1 text-xs rounded bg-[#4CC9C0] text-white hover:opacity-90"
  //         >
  //           📋 Update
  //         </button>
  //       </div>
  //     ),
  //   },
  // ];

  // const patientToCallBackColumns: Column[] = [
  //   {
  //     field: "srNo",
  //     headerName: "#",
  //     width: 70,
  //     sortable: true,
  //     // renderCell: (_row, index) => index + 1,
  //   },
  //   {
  //     field: "customer_phone_number_view",
  //     headerName: "Phone",
  //     minWidth: 120,
  //     sortable: true,
  //   },
  //   {
  //     field: "customer_city_view",
  //     headerName: "City",
  //     minWidth: 120,
  //     sortable: true,
  //   },
  //   {
  //     field: "customer_state_view",
  //     headerName: "State",
  //     minWidth: 120,
  //     sortable: true,
  //   },
  //   {
  //     field: "status",
  //     headerName: "Status",
  //     minWidth: 120,
  //     sortable: true,
  //   },
  //   {
  //     field: "notes",
  //     headerName: "Notes",
  //     minWidth: 200,
  //     renderCell: (row: any) => (
  //       <div
  //         style={{
  //           whiteSpace: "pre-wrap",
  //           wordWrap: "break-word",
  //           width: "100%",
  //           padding: "8px 0",
  //           lineHeight: "1.5",
  //         }}
  //       >
  //         {row.notes}
  //       </div>
  //     ),
  //   },
  //   {
  //     field: "source",
  //     headerName: "Source",
  //     minWidth: 120,
  //     sortable: true,
  //     renderCell: (row: any) => <span>{row.source}</span>,
  //   },
  //   {
  //     field: "action",
  //     headerName: "Action",
  //     width: 120,
  //     sortable: false,
  //     renderCell: (row: any) => (
  //       <div className="flex justify-center items-center h-full">
  //         <button
  //           onClick={(e) => {
  //             e.stopPropagation();
  //             setSelectedRow({ ...row, newStatus: row.status });
  //             setCallLeadsModal(true);
  //           }}
  //           className="px-2 py-1 text-xs rounded bg-[#4CC9C0] text-white hover:opacity-90"
  //         >
  //           📋 Update
  //         </button>
  //       </div>
  //     ),
  //   },
  // ];

  //   const totalValue = rows?.reduce((acc, row) => acc + row.value, 0);

  if (loading) {
    return <div className="bg-white p-6 rounded-lg shadow-md">Loading...</div>;
  }

  // if (error) {
  //   return <div className="bg-white p-6 rounded-lg shadow-md">Error: {error}</div>;
  // }

  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-[#194e5b] mb-2">
          New Patient Follow Up
        </h2>
        <div className="flex justify-between items-center my-4">
          <h3 className="text-lg font-semibold text-[#194e5b]">
            Clinic to follow up with patient
          </h3>
          <div className="text-lg font-semibold text-gray-700">
            {/* Total Value: ${totalValue.toLocaleString()} */}
          </div>
        </div>
        <div style={{ width: "100%" }}>
          <DataTable
            value={rows}
            dataKey="id"
            size="large"
            showGridlines
            // stripedRows
            tableStyle={{ minWidth: "100%", borderCollapse: "collapse" }}
            className="custom-table"
          >
            {/* ✅ Sr No */}
            <Column
              field="srNo"
              header="Sr No"
              body={(_, options) => options.rowIndex + 1}
              style={{
                width: "5%",
                whiteSpace: "nowrap", // 🔹 prevents wrapping
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />

            <Column
              field="customer_phone_number_view"
              header="Phone"
              style={{
                minWidth: "150px",
                maxWidth: "160px",
                whiteSpace: "nowrap", // 🔹 single line
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: 500,
              }}
            />

            <Column
              field="customer_city_view"
              header="City"
              style={{ minWidth: "140px" }}
            />

            <Column
              field="customer_state_view"
              header="State"
              style={{ minWidth: "140px" }}
            />

            {/* ✅ Status */}
            {/* <Column
              field="status"
              header="Status"
              style={{ minWidth: "130px" }}
            /> */}

            {/* ✅ Reason */}
            <Column
              field="notes"
              header="Reason"
              body={reasonBodyTemplate}
              style={{
                minWidth: "10%", // 🔹 reduced further
                maxWidth: "140px", // 🔹 tighter cap
                whiteSpace: "normal", // 🔹 Reason can wrap
                wordBreak: "break-word",
                lineHeight: "1.5",
              }}
            />
            {/* ✅ Source */}
            <Column
              field="source"
              header="Source"
              body={(row) => <span>{row.source}</span>}
              style={{ minWidth: "120px", textTransform: "capitalize" }}
            />

            {/* ✅ Action */}
            <Column
              header="Action"
              body={actionBodyTemplate}
              style={{ width: "10%", textAlign: "center" }}
            />
          </DataTable>

          {/* ✅ Custom Styles */}
          <style jsx global>{`
           

            .p-datatable.custom-table {
              border-radius: 10px;
              overflow: hidden;
              box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
              font-size: 14px;
            }

            .p-datatable.custom-table .p-datatable-thead > tr > th {
              background: #f9fafb;
              font-weight: 600;
              color: #111827;
              padding: 12px 16px;
              border-bottom: 1px solid #e5e7eb;
              white-space: nowrap;
              vertical-align: middle;
            }

            .p-datatable.custom-table .p-datatable-tbody > tr > td {
              padding: 12px 16px;
              border-bottom: 1px solid #e5e7eb;
              vertical-align: middle;
              white-space: normal;
              word-break: break-word;
            }

            .p-datatable.custom-table .p-datatable-tbody > tr:last-child > td {
              border-bottom: none;
            }

            .p-datatable-wrapper {
              overflow-x: hidden !important;
              overflow-y: hidden !important;
            }

            @media (max-width: 768px) {
              .p-datatable.custom-table {
                font-size: 12px;
              }
            }
          `}</style>
          {/* <DataTable
            columns={columns}
            rows={rows}
            // selectable
            // pagination
          /> */}
          {/* <DataGrid
            rows={rows}
            columns={columns}
            getRowHeight={() => "auto"}
            hideFooter
            disableColumnMenu
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "flex-start",
                padding: "8px",
                whiteSpace: "normal",
              },
              "& .MuiDataGrid-row": {
                "&.Mui-selected": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.12)",
                  },
                },
              },
            }}
            //   disableSelectionOnClick
          /> */}
        </div>
        <div className="flex justify-between items-center my-4">
          <h3 className="text-lg font-semibold text-[#194e5b]">
            Patient to call back
          </h3>
          <div className="text-lg font-semibold text-gray-700">
            {/* Total Value: ${totalValue.toLocaleString()} */}
          </div>
        </div>
        <div style={{ width: "100%" }}>
          <DataTable
            value={patientToCallBackRows}
            dataKey="id"
            size="large"
            showGridlines
            // stripedRows
            tableStyle={{ minWidth: "100%", borderCollapse: "collapse" }}
            className="custom-table"
          >
            {/* ✅ Sr No */}
            <Column
              field="srNo"
              header="Sr No"
              body={(_, options) => options.rowIndex + 1}
              style={{
                width: "5%",
                whiteSpace: "nowrap", // 🔹 prevents wrapping
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            />

            <Column
              field="customer_phone_number_view"
              header="Phone"
              style={{
                minWidth: "150px",
                maxWidth: "160px",
                whiteSpace: "nowrap", // 🔹 single line
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: 500,
              }}
            />

            <Column
              field="customer_city_view"
              header="City"
              style={{ minWidth: "140px" }}
            />

            <Column
              field="customer_state_view"
              header="State"
              style={{ minWidth: "140px" }}
            />

            {/* ✅ Status */}
            {/* <Column
              field="status"
              header="Status"
              style={{ minWidth: "130px" }}
            /> */}

            {/* ✅ Reason */}
            <Column
              field="notes"
              header="Notes"
              // body={reasonBodyTemplate}
              style={{
                minWidth: "10%", // 🔹 reduced further
                maxWidth: "140px", // 🔹 tighter cap
                whiteSpace: "normal", // 🔹 Reason can wrap
                wordBreak: "break-word",
                lineHeight: "1.5",
              }}
            />
            {/* ✅ Source */}
            <Column
              field="source"
              header="Source"
              body={(row) => <span>{row.source}</span>}
              style={{ minWidth: "120px", textTransform: "capitalize" }}
            />

            {/* ✅ Action */}
            <Column
              header="Action"
              body={actionBodyTemplate2}
              style={{ width: "10%", textAlign: "center" }}
            />
          </DataTable>

          {/* ✅ Custom Styles */}
          <style jsx global>{`
            

            .p-datatable.custom-table {
              border-radius: 10px;
              overflow: hidden;
              box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
              font-size: 14px;
            }

            .p-datatable.custom-table .p-datatable-thead > tr > th {
              background: #f9fafb;
              font-weight: 600;
              color: #111827;
              padding: 12px 16px;
              border-bottom: 1px solid #e5e7eb;
              white-space: nowrap;
              vertical-align: middle;
            }

            .p-datatable.custom-table .p-datatable-tbody > tr > td {
              padding: 12px 16px;
              border-bottom: 1px solid #e5e7eb;
              vertical-align: middle;
              white-space: normal;
              word-break: break-word;
            }

            .p-datatable.custom-table .p-datatable-tbody > tr:last-child > td {
              border-bottom: none;
            }

            .p-datatable-wrapper {
              overflow-x: hidden !important;
              overflow-y: hidden !important;
            }

            @media (max-width: 768px) {
              .p-datatable.custom-table {
                font-size: 12px;
              }
            }
          `}</style>
          {/* <DataTable
            columns={patientToCallBackColumns}
            rows={patientToCallBackRows}
            // selectable
            // pagination
          /> */}
          {/* <DataGrid
            rows={patientToCallBackRows}
            columns={patientToCallBackColumns}
            hideFooter
            disableColumnMenu
            getRowHeight={() => "auto"}
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "flex-start",
                padding: "8px",
                whiteSpace: "normal",
              },
              "& .MuiDataGrid-row": {
                "&.Mui-selected": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.12)",
                  },
                },
              },
            }}
            //   disableSelectionOnClick
          /> */}
        </div>
        {/* <div className="text-sm text-gray-600 mt-2">
        <span className="mr-4">📞 = Phone Call</span>
        <span className="mr-4">🔊 = Recording Available (clickable)</span>
        <span className="mr-4">📅 = Online Booking</span>
        <span className="mr-4">🎁 = Rewards</span>
        <span className="mr-4">👥 = Referral</span>
      </div> */}
      </div>
      <UpdateCallLeads
        open={callLeadsModal}
        onClose={() => setCallLeadsModal(false)}
        clinicData={clinicDatax}
        dateFilter={dateFilter}
        initialData={selectedRow}
        onSave={fetchBothAPI}
      />
    </>
  );
};

export default PatientFollowUp;
